import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Printer } from 'lucide-react';
import { CustomHAOrderModal } from './CustomHAOrdersPage';
import HASpecPicker from '../../components/HASpecPicker';
import POPrintModal from './POPrintModal';
import ShippingLabelModal from '../../components/ShippingLabelModal';
import VendorAutocomplete from '../../components/VendorAutocomplete';
import { formatSpecLong } from '../../lib/haSpecs';

import useEscapeKey from '../../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const PO_STATUS_STYLES = {
  draft:            'bg-slate-100 text-slate-600',
  approved:         'bg-amber-100 text-amber-800',
  ordered:          'bg-blue-100 text-blue-800',
  partial_received: 'bg-purple-100 text-purple-800',
  received:         'bg-emerald-100 text-emerald-800',
  closed:           'bg-slate-200 text-slate-700',
  cancelled:        'bg-rose-100 text-rose-800 line-through',
};

export default function ProcurementPage() {
  const [pos, setPos] = useState([]);
  const [open, setOpen] = useState(null);   // po_no
  const [creating, setCreating] = useState(false);
  const [customHAOpen, setCustomHAOpen] = useState(false);

  const load = useCallback(async () => {
    const r = await axios.get(`${API}/ha/purchase-orders`);
    setPos(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-5" data-testid="ha-procurement-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Procurement</h1>
          <p className="text-[11px] text-slate-500 mt-0.5">Purchase orders → goods receipts. Posting a GRN auto-spawns serial items &amp; updates stock.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="ha-po-new-custom-ha"
            onClick={() => setCustomHAOpen(true)}
            className="px-3 py-1.5 text-xs font-semibold bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 rounded-md shadow-sm"
          >+ Custom HA Order</button>
          <button
            data-testid="ha-po-new"
            onClick={() => setCreating(true)}
            className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 text-white rounded-md shadow-sm"
          >+ New PO</button>
        </div>
      </div>

      {/* Cross-link to the Vendors master so a fresh clinic can see how to
          set up the pre-requisite before hitting the "no vendors" wall in
          the PO modal. Hidden once at least one vendor exists to avoid
          clutter. */}
      <div className="mb-3 flex items-center gap-3 text-[11px] text-slate-500">
        <span>Need to manage suppliers?</span>
        <Link
          to="/ha/vendors"
          data-testid="ha-procurement-vendors-link"
          className="font-semibold text-indigo-600 hover:underline"
        >Open Vendors master →</Link>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left">PO No</th>
              <th className="px-3 py-2 text-left">Vendor</th>
              <th className="px-3 py-2 text-right">Lines</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {pos.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-slate-400 italic text-xs">No purchase orders yet.</td></tr>
            )}
            {pos.map(po => (
              <tr key={po.po_no} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`ha-po-row-${po.po_no}`}>
                <td className="px-3 py-2 font-mono text-xs font-bold">{po.po_no}</td>
                <td className="px-3 py-2">{po.vendor_name || po.vendor_id}</td>
                <td className="px-3 py-2 text-right tabular-nums">{po.lines?.length || 0}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtINR(po.total)}</td>
                <td className="px-3 py-2"><span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PO_STATUS_STYLES[po.status] || 'bg-slate-100'}`}>{(po.status.replace('_',' ') || '').toUpperCase()}</span></td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{po.created_at ? new Date(po.created_at).toLocaleDateString('en-IN') : ''}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setOpen(po.po_no)} data-testid={`ha-po-open-${po.po_no}`} className="text-[10px] text-indigo-600 font-semibold hover:underline">Open →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && <CreatePOModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
      {customHAOpen && <CustomHAOrderModal defaultTarget="vendor" onClose={() => setCustomHAOpen(false)} onSaved={() => setCustomHAOpen(false)} />}
      {open && <PODetailDrawer poNo={open} onClose={() => setOpen(null)} onChanged={load} />}
    </div>
  );
}

function CreatePOModal({ onClose, onCreated }) {
  useEscapeKey(onClose);
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [branch, setBranch] = useState('');
  const [vendor, setVendor] = useState('');
  const [expected, setExpected] = useState('');
  const [lines, setLines] = useState([{ _key: Math.random().toString(36).slice(2), product_id: '', qty: 1, unit_cost: 0, gst_rate: 18 }]);
  const [roundOff, setRoundOff] = useState(0);
  const [err, setErr] = useState('');
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      const [b, v, p] = await Promise.all([
        axios.get(`${API}/branches`),
        axios.get(`${API}/vendors?active=true`),
        axios.get(`${API}/ha/products?active=true`),
      ]);
      setBranches(b.data);
      setVendors(v.data);
      setProducts(p.data);
      if (b.data[0]) setBranch(b.data[0].branch_id);
      if (v.data[0]) setVendor(v.data[0].vendor_id);
      setLoaded(true);
      // If the clinic has ZERO vendors, drop the user straight into the
      // Quick-Add-Vendor form. Blank dropdowns + a scary "Vendor not found"
      // error banner were the reported UX on 2026-07-30 — proactive
      // guidance is the fix.
      if ((v.data || []).length === 0) setShowVendorForm(true);
    })();
  }, []);

  const total = lines.reduce((a, l) => a + l.qty * l.unit_cost * (1 + l.gst_rate / 100), 0);
  // 2026-02-19 — Operator-entered round-off (±₹10 max) that aligns the
  // saved PO total with the vendor's printed invoice. Clamped on blur,
  // not on every keystroke, so typing "-10.00" is painless.
  const roundOffNum = Math.min(10, Math.max(-10, Number(roundOff) || 0));
  const finalTotal = total + roundOffNum;

  // Auto-suggest Round Off based on vendor's historical rounding pattern.
  // Fires when (vendor, total) stabilises; only prefills when the operator
  // hasn't manually touched the field yet (dirtyRef is false). Any manual
  // keystroke flips the dirty flag so we never clobber their entry.
  const [suggestion, setSuggestion] = useState(null);
  const dirtyRef = React.useRef(false);
  const handleRoundOffChange = (v) => {
    dirtyRef.current = true;
    setRoundOff(v);
  };
  useEffect(() => {
    if (!vendor || total <= 0) { setSuggestion(null); return; }
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const r = await axios.get(
          `${API}/ha/vendors/${vendor}/round-off-suggestion`,
          { params: { preview_total: total } },
        );
        if (cancelled) return;
        const s = r.data || null;
        setSuggestion(s);
        if (s && s.suggested_round_off !== 0 && !dirtyRef.current) {
          setRoundOff(s.suggested_round_off);
        }
      } catch { /* non-fatal — operator can still type manually */ }
    }, 400);
    return () => { cancelled = true; clearTimeout(h); };
  }, [vendor, total]);

  // Validation state — kept as booleans so the submit button + inline hints
  // stay in sync without a separate "server-said-so" round-trip.
  const hasVendor = !!vendor;
  const hasLines = lines.some((l) => l.product_id && l.qty > 0);
  const canSubmit = loaded && hasVendor && hasLines && !saving;

  const submit = async () => {
    setErr('');
    // Client-side gates so we never surface backend errors that already
    // have a clearer UI-level explanation.
    if (!hasVendor) {
      setErr(vendors.length === 0
        ? 'Add a vendor first — click "+ Add your first vendor" above.'
        : 'Pick a vendor from the dropdown to continue.');
      return;
    }
    if (!hasLines) { setErr('Add at least one product line with a quantity.'); return; }
    setSaving(true);
    try {
      const body = {
        branch_id: branch,
        vendor_id: vendor,
        expected_date: expected || null,
        round_off: roundOffNum,
        lines: lines.filter(l => l.product_id && l.qty > 0).map(l => {
          // Strip local-only bookkeeping fields before shipping to the API.
          // `_key` + `_form_factor` are picker-driven, not part of POLine.
          const { _key, _form_factor, spec, ...rest } = l;
          return spec && Object.keys(spec).length ? { ...rest, spec } : rest;
        }),
      };
      await axios.post(`${API}/ha/purchase-orders`, body);
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // The backend's "Vendor not found" is 99% a stale vendor_id on the
      // client (deleted vendor kept in memory). Surface a nudge instead of
      // the raw string.
      if (typeof d === 'string' && (d || '').toLowerCase().includes('vendor')) {
        setErr(`${d}. Pick a different vendor, or use "+ Add vendor" to create a fresh one.`);
      } else {
        setErr(typeof d === 'string' ? d : 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ha-po-new-modal">
        <h2 className="text-lg font-bold mb-4">New Purchase Order</h2>
        {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-3">{err}</div>}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <Field label="Branch *">
            <select value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="ha-po-branch" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
              {branches.map(b => <option key={b.branch_id} value={b.branch_id}>{b.name}</option>)}
            </select>
          </Field>
          <Field label="Vendor *">
            <div className="flex gap-1">
              <div className="flex-1">
                <VendorAutocomplete
                  vendors={vendors}
                  value={vendor}
                  onChange={(id) => { setVendor(id); setErr(''); }}
                  onCreateNew={() => setShowVendorForm(true)}
                  disabled={vendors.length === 0}
                  testid="ha-po-vendor"
                  placeholder={vendors.length === 0 ? '— no vendors yet —' : 'Search vendor by name, state or GSTIN…'}
                />
              </div>
              <button
                type="button"
                onClick={() => setShowVendorForm(true)}
                data-testid="ha-po-vendor-new"
                title="Quick-add a new vendor"
                className="px-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded shadow-sm"
              >+</button>
            </div>
          </Field>
          <Field label="Expected Date">
            <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
          </Field>
        </div>

        {/* Empty-state coach — surfaces the fix path immediately instead of
            letting the user submit and see "Vendor not found" from the API. */}
        {loaded && vendors.length === 0 && (
          <div
            className="mb-4 border border-amber-300 bg-amber-50 rounded-lg p-3 flex items-start gap-3"
            data-testid="ha-po-empty-vendors"
          >
            <div className="text-2xl">📦</div>
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-900">No vendors yet</div>
              <div className="text-[11px] text-amber-800 leading-snug">
                Purchase orders need at least one supplier. Add one now — it takes 30 seconds
                and you&apos;ll be able to reuse it for every future PO.
              </div>
              <div className="mt-2 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowVendorForm(true)}
                  data-testid="ha-po-add-first-vendor"
                  className="px-2.5 py-1 text-[11px] font-bold bg-amber-500 hover:bg-amber-600 text-white rounded shadow-sm"
                >
                  + Add your first vendor
                </button>
                <Link
                  to="/ha/vendors"
                  onClick={onClose}
                  className="text-[11px] font-semibold text-indigo-600 hover:underline"
                >
                  Or open the Vendors master →
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="px-2 py-1 text-left">Product</th>
                <th className="px-2 py-1 text-right">Qty</th>
                <th className="px-2 py-1 text-right">Unit Cost</th>
                <th className="px-2 py-1 text-right">GST %</th>
                <th className="px-2 py-1 text-right">Line Total</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <React.Fragment key={l._key || `PO-${i}`}>
                <tr className="border-t border-slate-100">
                  <td className="px-2 py-1">
                    <select value={l.product_id} onChange={(e) => {
                      const copy = [...lines];
                      copy[i] = { ...copy[i], product_id: e.target.value };
                      const p = products.find(pp => pp.product_id === e.target.value);
                      if (p) { copy[i].unit_cost = p.cost; copy[i].gst_rate = p.gst_rate; copy[i]._form_factor = p.form_factor; }
                      setLines(copy);
                    }} data-testid={`ha-po-line-${i}-prod`} className="w-full border border-slate-300 rounded px-1 py-0.5 text-xs">
                      <option value="">—</option>
                      {products.map(p => <option key={p.product_id} value={p.product_id}>{`${p.brand} ${p.model}`}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1 text-right"><input type="number" min={1} value={l.qty} onChange={(e) => { const c=[...lines]; c[i]={...c[i],qty:Number(e.target.value)}; setLines(c); }} data-testid={`ha-po-line-${i}-qty`} className="w-16 border border-slate-300 rounded px-1 py-0.5 text-right text-xs" /></td>
                  <td className="px-2 py-1 text-right"><input type="number" value={l.unit_cost} onChange={(e) => { const c=[...lines]; c[i]={...c[i],unit_cost:Number(e.target.value)}; setLines(c); }} className="w-24 border border-slate-300 rounded px-1 py-0.5 text-right text-xs" /></td>
                  <td className="px-2 py-1 text-right"><input type="number" value={l.gst_rate} onChange={(e) => { const c=[...lines]; c[i]={...c[i],gst_rate:Number(e.target.value)}; setLines(c); }} className="w-12 border border-slate-300 rounded px-1 py-0.5 text-right text-xs" /></td>
                  <td className="px-2 py-1 text-right tabular-nums text-xs">{fmtINR(l.qty * l.unit_cost * (1 + l.gst_rate / 100))}</td>
                  <td className="px-2 py-1 text-right">
                    {lines.length > 1 && <button onClick={() => setLines(lines.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700 text-xs">×</button>}
                  </td>
                </tr>
                {/* Vendor asks for the exact SKU variant on every PO line
                    — colour + power + wire/tube length. Same picker,
                    driven by the product's form_factor. Split lines
                    when the PO mixes variants (e.g. 3 Beige + 2 Champagne). */}
                {l.product_id && (l._form_factor || products.find(pp => pp.product_id === l.product_id)?.form_factor) && (
                  <tr className="border-t-0"><td colSpan={6} className="px-2 pt-0 pb-2">
                    <div className="rounded border border-slate-200 bg-slate-50 p-2">
                      <HASpecPicker
                        deviceType={l._form_factor || products.find(pp => pp.product_id === l.product_id)?.form_factor}
                        side="R"
                        value={l.spec || {}}
                        onChange={(sp) => { const c=[...lines]; c[i] = {...c[i], spec: sp}; setLines(c); }}
                        testIdPrefix={`ha-po-line-${i}-spec`}
                        title="Line spec — colour · power · length"
                        compact
                      />
                    </div>
                  </td></tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-3">
          <button onClick={() => setLines([...lines, { _key: Math.random().toString(36).slice(2), product_id: '', qty: 1, unit_cost: 0, gst_rate: 18 }])} data-testid="ha-po-add-line" className="text-xs text-indigo-600 font-semibold hover:underline">+ Add line</button>
          <div className="text-sm" data-testid="ha-po-total-incl-gst"><span className="text-slate-500 mr-2">Total (incl GST):</span><span className="font-bold tabular-nums">{fmtINR(total)}</span></div>
        </div>

        {/* Round-off reconciliation row. Lets the operator nudge the saved
            PO total up or down by ±₹10 so it matches the vendor invoice to
            the paisa. The GRN / linked vendor invoice downstream read
            `po.total` which is already the FINAL total (after round-off). */}
        <div className="mt-2 flex items-center justify-end gap-3 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Round Off (±₹10)</span>
            <input
              type="number"
              step="0.01"
              min={-10}
              max={10}
              value={roundOff}
              onChange={(e) => handleRoundOffChange(e.target.value)}
              onBlur={(e) => {
                const n = Math.min(10, Math.max(-10, Number(e.target.value) || 0));
                setRoundOff(n);
              }}
              placeholder="0.00"
              data-testid="ha-po-round-off"
              className="w-24 border border-slate-300 rounded px-2 py-1 text-right text-xs"
            />
          </label>
        </div>
        {suggestion && suggestion.suggested_round_off !== 0 && !dirtyRef.current && (
          <div className="mt-1 flex items-center justify-end text-[10px] text-indigo-600" data-testid="ha-po-round-off-suggestion">
            <span className="italic">
              Auto-filled {suggestion.suggested_round_off >= 0 ? '+' : ''}{Number(suggestion.suggested_round_off).toFixed(2)} — this vendor&apos;s last {suggestion.sample_size} POs round to the nearest ₹ ({suggestion.confidence} confidence)
            </span>
          </div>
        )}
        <div className="mt-1 flex items-center justify-end text-sm" data-testid="ha-po-final-total">
          <span className="text-slate-500 mr-2">Final Total:</span>
          <span className="text-indigo-700 font-bold tabular-nums text-base">{fmtINR(finalTotal)}</span>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            title={
              !hasVendor ? 'Pick a vendor first'
                : !hasLines ? 'Add at least one product line'
                : ''
            }
            data-testid="ha-po-save"
            className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded"
          >
            {saving ? 'Creating…' : 'Create PO'}
          </button>
        </div>
      </div>
      {showVendorForm && (
        <QuickAddVendor
          onClose={() => setShowVendorForm(false)}
          onCreated={(newVendor) => {
            setVendors((vs) => [...vs, newVendor].sort((a, b) => a.name.localeCompare(b.name)));
            setVendor(newVendor.vendor_id);
            setShowVendorForm(false);
          }}
        />
      )}
    </div>
  );
}

function QuickAddVendor({ onClose, onCreated }) {
  useEscapeKey(onClose);
  const [f, setF] = useState({
    name: '', contact_person: '', phone: '', email: '',
    gstin: '', city: '', state: '', address: '', payment_terms_days: 30,
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { setErr('Vendor name is required'); return; }
    setErr(''); setSaving(true);
    try {
      const r = await axios.post(`${API}/vendors`, f);
      onCreated(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (status === 403) {
        // Only clinic_owner + inventory_manager (+super_admin/founder) can
        // create vendors. Front desk / audiologist / accounts see this.
        setErr('Your role can\u2019t add vendors — ask a clinic owner or inventory manager to add this supplier and try again.');
      } else if (status === 401) {
        setErr('Your session expired. Please sign in again and retry.');
      } else {
        setErr(typeof d === 'string' ? d : 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
     
      data-testid="ha-vendor-quickadd-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-bold mb-3">Add New Vendor</h3>
        {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-2">{err}</div>}
        <div className="grid grid-cols-2 gap-2 text-sm">
          <Field label="Vendor Name *">
            <input
              autoFocus
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              data-testid="ha-vendor-name"
              placeholder="e.g. Phonak India Pvt Ltd"
              className="w-full border border-slate-300 rounded px-2 py-1"
            />
          </Field>
          <Field label="Contact Person">
            <input value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })} data-testid="ha-vendor-contact" className="w-full border border-slate-300 rounded px-2 py-1" />
          </Field>
          <Field label="Phone">
            <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="ha-vendor-phone" placeholder="+91…" className="w-full border border-slate-300 rounded px-2 py-1" />
          </Field>
          <Field label="Email">
            <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="ha-vendor-email" className="w-full border border-slate-300 rounded px-2 py-1" />
          </Field>
          <Field label="GSTIN">
            <input value={f.gstin} onChange={(e) => setF({ ...f, gstin: (e.target.value || '').toUpperCase() })} data-testid="ha-vendor-gstin" placeholder="15-char GSTIN" className="w-full border border-slate-300 rounded px-2 py-1 font-mono" />
          </Field>
          <Field label="City">
            <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="ha-vendor-city" placeholder="Bengaluru" className="w-full border border-slate-300 rounded px-2 py-1" />
          </Field>
          <Field label="State">
            <input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="ha-vendor-state" placeholder="Karnataka" className="w-full border border-slate-300 rounded px-2 py-1" />
          </Field>
          <Field label="Payment Terms (days)">
            <input type="number" value={f.payment_terms_days} onChange={(e) => setF({ ...f, payment_terms_days: Number(e.target.value) })} data-testid="ha-vendor-terms" className="w-full border border-slate-300 rounded px-2 py-1" />
          </Field>
          <label className="block col-span-2">
            <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Address</span>
            <textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="ha-vendor-address" rows={2} className="w-full border border-slate-300 rounded px-2 py-1" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="ha-vendor-save"
            className="px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white rounded shadow-sm"
          >{saving ? 'Saving…' : 'Save Vendor'}</button>
        </div>
      </div>
    </div>
  );
}

// Exported so sibling modals (e.g. CustomHAOrdersPage's `+ Add new vendor`
// footer action, iter-129) can reuse this instead of forking their own
// quick-add form.
export { QuickAddVendor };

function PODetailDrawer({ poNo, onClose, onChanged }) {
  useEscapeKey(onClose);
  const [po, setPO] = useState(null);
  const [products, setProducts] = useState({});
  const [vendor, setVendor] = useState(null);
  const [branch, setBranch] = useState(null);
  const [clinicName, setClinicName] = useState('');
  const [grnOpen, setGrnOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [shipLabelOpen, setShipLabelOpen] = useState(false);
  const [err, setErr] = useState('');
  // `pending` holds the target status currently being POSTed so we can
  // both disable the button *and* show a spinner label like "Approving…".
  // Without this the click looked like a no-op and users hammered it.
  const [pending, setPending] = useState(null);

  const load = useCallback(async () => {
    const [poData, prods] = await Promise.all([
      axios.get(`${API}/ha/purchase-orders/${poNo}`),
      axios.get(`${API}/ha/products`),
    ]);
    setPO(poData.data);
    setProducts(Object.fromEntries(prods.data.map(p => [p.product_id, p])));
    // Fetch vendor so the print view can show GSTIN / address / phone
    // without a second trip after Print is clicked.
    try {
      const vRes = await axios.get(`${API}/vendors/${poData.data.vendor_id}`);
      setVendor(vRes.data);
    } catch { /* non-fatal — print falls back to vendor_name alone */ }
    // Branch (FROM) + clinic name — fetched from /api/branches (list carries
    // clinic_name alongside each branch). Used by the Shipping Label modal.
    try {
      const bRes = await axios.get(`${API}/branches`);
      const match = (bRes.data || []).find(b => b.branch_id === poData.data.branch_id);
      if (match) {
        setBranch(match);
        setClinicName(match.clinic_name || '');
      }
    } catch { /* non-fatal — label falls back to "Audinexa Clinic" */ }
  }, [poNo]);

  useEffect(() => { load(); }, [load]);

  const transition = async (to_status) => {
    // Prevent double-fire: if a request is already in flight, ignore
    // subsequent clicks completely. The double-fire pattern was
    // causing hangs and duplicate mongo writes.
    if (pending) return;
    setErr('');
    setPending(to_status);
    try {
      await axios.post(`${API}/ha/purchase-orders/${poNo}/status`, { to_status });
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Action failed');
    } finally {
      setPending(null);
    }
  };

  if (!po) return null;
  const NEXT_ACTIONS = {
    draft: [{ to: 'approved', label: 'Approve', pendingLabel: 'Approving…', style: 'bg-amber-500' }, { to: 'cancelled', label: 'Cancel', pendingLabel: 'Cancelling…', style: 'bg-slate-500' }],
    approved: [{ to: 'ordered', label: 'Mark Ordered', pendingLabel: 'Marking…', style: 'bg-blue-600' }, { to: 'cancelled', label: 'Cancel', pendingLabel: 'Cancelling…', style: 'bg-slate-500' }],
    ordered: [],
    partial_received: [{ to: 'closed', label: 'Close (waive remainder)', pendingLabel: 'Closing…', style: 'bg-slate-700' }],
    received: [{ to: 'closed', label: 'Close PO', pendingLabel: 'Closing…', style: 'bg-emerald-600' }],
    closed: [],
    cancelled: [],
  };
  const canReceive = ['approved', 'ordered', 'partial_received'].includes(po.status);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="ha-po-detail-drawer">
      <div className="flex-1 bg-black/40" />
      <aside className="w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        <div className="border-b border-slate-200 p-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Purchase Order</div>
            <div className="text-lg font-bold font-mono">{po.po_no}</div>
            <div className="text-[11px] text-slate-500">{po.vendor_name} · <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PO_STATUS_STYLES[po.status]}`}>{(po.status.replace('_',' ') || '').toUpperCase()}</span></div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPrintOpen(true)}
              data-testid="ha-po-print-open"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded shadow-sm"
              title="Open a printable PO (A4) with the full Round Off / Final Total breakdown">
              <Printer size={11} /> Print
            </button>
            <button
              onClick={() => setShipLabelOpen(true)}
              data-testid="ha-po-print-shipping-label"
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-white border border-amber-400 hover:bg-amber-50 text-amber-800 rounded shadow-sm"
              title="Print an A5 shipping label with FROM (branch) → TO (vendor) addresses">
              <Printer size={11} /> Shipping Label
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-800 text-2xl leading-none" data-testid="ha-po-close">×</button>
          </div>
        </div>

        {err && <div className="mx-4 mt-3 text-xs text-rose-700 bg-rose-50 rounded p-2">{err}</div>}

        <div className="p-4 space-y-3">
          <div className="bg-slate-50 rounded p-3 text-sm">
            <table className="w-full">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left pb-1">Product</th><th className="text-right pb-1">Qty</th><th className="text-right pb-1">Unit</th><th className="text-right pb-1">GST</th><th className="text-right pb-1">Total</th></tr>
              </thead>
              <tbody>
                {po.lines.map((ln, i) => {
                  const p = products[ln.product_id];
                  return (
                    <tr key={`${ln.product_id || 'p'}-${i}`} className="border-t border-slate-200">
                      <td className="py-1">
                        {p ? `${p.brand} ${p.model}` : ln.product_id}
                        {ln.spec && formatSpecLong(ln.spec) && (
                          <span
                            className="ml-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5"
                            data-testid={`ha-po-line-${i}-spec-badge`}
                            title="Requested SKU variant"
                          >
                            {formatSpecLong(ln.spec)}
                          </span>
                        )}
                      </td>
                      <td className="py-1 text-right tabular-nums">{ln.qty}</td>
                      <td className="py-1 text-right tabular-nums">{fmtINR(ln.unit_cost)}</td>
                      <td className="py-1 text-right tabular-nums">{ln.gst_rate}%</td>
                      <td className="py-1 text-right tabular-nums">{fmtINR(ln.qty * ln.unit_cost * (1 + ln.gst_rate / 100))}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200">
                  <td colSpan={4} className="py-1 text-right text-xs text-slate-500">Subtotal + GST</td>
                  <td className="py-1 text-right tabular-nums text-xs" data-testid="ha-po-drawer-pre-roundoff">
                    {fmtINR((po.subtotal || 0) + (po.gst_amount || 0))}
                  </td>
                </tr>
                {Number(po.round_off || 0) !== 0 && (
                  <tr className="border-t border-slate-100" data-testid="ha-po-drawer-round-off-row">
                    <td colSpan={4} className="py-1 text-right text-xs text-slate-500">Round Off</td>
                    <td className="py-1 text-right tabular-nums text-xs">
                      {po.round_off >= 0 ? '+' : ''}{fmtINR(po.round_off)}
                    </td>
                  </tr>
                )}
                <tr className="border-t border-slate-300 font-bold">
                  <td colSpan={4} className="py-2 text-right">Final Total</td>
                  <td className="py-2 text-right tabular-nums" data-testid="ha-po-drawer-final-total">{fmtINR(po.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {(NEXT_ACTIONS[po.status] || []).map(a => {
              const isThisPending = pending === a.to;
              const anyPending = !!pending;
              return (
                <button
                  key={a.to}
                  onClick={() => transition(a.to)}
                  disabled={anyPending}
                  data-testid={`ha-po-trans-${a.to}`}
                  className={`px-3 py-1.5 text-xs font-semibold text-white rounded ${a.style} hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5`}
                >
                  {isThisPending && (
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                    </svg>
                  )}
                  {isThisPending ? a.pendingLabel : a.label}
                </button>
              );
            })}
            {canReceive && (
              <button onClick={() => setGrnOpen(true)} disabled={!!pending} data-testid="ha-po-receive" className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded shadow">Receive Goods (GRN)</button>
            )}
          </div>
        </div>

        {grnOpen && <GRNModal po={po} products={products} onClose={() => setGrnOpen(false)} onSaved={() => { setGrnOpen(false); load(); onChanged && onChanged(); }} />}
      </aside>
      {printOpen && po && (
        <POPrintModal
          po={po}
          products={products}
          onClose={() => setPrintOpen(false)}
        />
      )}
      {shipLabelOpen && po && (
        <ShippingLabelModal
          clinicName={clinicName}
          branch={branch}
          vendor={vendor}
          referenceLabel="PO Number"
          referenceNo={po.po_no}
          date={po.created_at}
          vendorFixHref={`/ha/vendors`}
          branchFixHref={`/settings`}
          onClose={() => setShipLabelOpen(false)}
        />
      )}
    </div>
  );
}

function GRNModal({ po, products, onClose, onSaved }) {
  useEscapeKey(onClose);
  const [lines, setLines] = useState(
    po.lines.map(ln => {
      const p = products[ln.product_id] || {};
      return {
        product_id: ln.product_id,
        qty_received: ln.qty,
        serial_nos: p.is_serialised ? Array(ln.qty).fill('') : [],
        is_serialised: p.is_serialised,
        label: p ? `${p.brand} ${p.model}` : ln.product_id,
      };
    })
  );
  const [invRef, setInvRef] = useState('');
  const [err, setErr] = useState('');

  const submit = async () => {
    setErr('');
    try {
      // Validate: serialised lines need exactly qty_received serials, all non-empty
      for (const ln of lines) {
        if (ln.is_serialised) {
          const clean = ln.serial_nos.filter(s => s.trim());
          if (clean.length !== ln.qty_received) {
            setErr(`Provide ${ln.qty_received} serial number(s) for ${ln.label}`);
            return;
          }
        }
      }
      const body = {
        po_no: po.po_no,
        vendor_invoice_ref: invRef || null,
        lines: lines.map(l => ({
          product_id: l.product_id,
          qty_received: l.qty_received,
          serial_nos: l.is_serialised ? l.serial_nos.map(s => s.trim()).filter(Boolean) : [],
        })),
      };
      await axios.post(`${API}/ha/grns`, body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'GRN submission failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="ha-grn-modal">
        <h2 className="text-lg font-bold mb-1">Receive Goods — {po.po_no}</h2>
        <p className="text-[11px] text-slate-500 mb-3">Posting will spawn a serial item per scanned serial (IN_STOCK) and add to accessory stock. This action is logged.</p>

        {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-3">{err}</div>}

        <label className="block mb-3">
          <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">Vendor Invoice Ref (optional)</span>
          <input value={invRef} onChange={(e) => setInvRef(e.target.value)} data-testid="ha-grn-inv-ref" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
        </label>

        <div className="space-y-4">
          {lines.map((ln, i) => (
            <div key={`${ln.product_id || 'grn'}-${i}`} className="border border-slate-200 rounded-md p-3" data-testid={`ha-grn-line-${i}`}>
              <div className="flex items-center justify-between mb-2">
                <div><span className="font-semibold">{ln.label}</span>{ln.is_serialised && <span className="text-[10px] ml-2 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-semibold">SERIALISED</span>}</div>
                <div className="text-xs">Qty received: <input type="number" min={0} value={ln.qty_received} onChange={(e) => {
                  const copy = [...lines];
                  const q = Number(e.target.value);
                  copy[i] = { ...copy[i], qty_received: q };
                  if (copy[i].is_serialised) copy[i].serial_nos = Array(q).fill('').map((_, k) => copy[i].serial_nos[k] || '');
                  setLines(copy);
                }} data-testid={`ha-grn-line-${i}-qty`} className="ml-2 w-20 border border-slate-300 rounded px-1 py-0.5 text-right text-xs" /></div>
              </div>
              {ln.is_serialised && ln.qty_received > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {ln.serial_nos.map((sn, j) => (
                    <input
                      key={`${ln.product_id || 'p'}-${i}-${j}`}
                      value={sn}
                      onChange={(e) => {
                        const copy = [...lines];
                        const sns = [...copy[i].serial_nos];
                        sns[j] = e.target.value;
                        copy[i] = { ...copy[i], serial_nos: sns };
                        setLines(copy);
                      }}
                      placeholder={`Serial #${j + 1}`}
                      data-testid={`ha-grn-line-${i}-serial-${j}`}
                      className="border border-slate-300 rounded px-2 py-1 text-xs font-mono"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
          <button onClick={submit} data-testid="ha-grn-save" className="px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded shadow">Post GRN &amp; Receive Stock</button>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5 font-semibold">{label}</span>
    {children}
  </label>
);
